import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, X, Lock, Calendar } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ClearReportRangeButton
 * ----------------------
 * Admin-only red pill button visible in the Withdraw / Deposit Summary
 * corner. Opens a modal that lets admin pick:
 *   • date range (from → to)
 *   • which types (deposit / withdraw / all)
 *   • which statuses (pending / paid / cancelled / all)
 * Requires admin login password. Hits POST /api/report-entries/clear-range.
 */
const ClearReportRangeButton = ({ adminId, onCleared }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [types, setTypes] = useState({ deposit: false, withdraw: false, all: true });
  const [statuses, setStatuses] = useState({ pending: false, paid: false, cancelled: false, done: false, all: true });

  const toggle = (setter, key) => setter(prev => {
    // "all" is exclusive with individual entries
    if (key === 'all') return { all: true };
    const next = { ...prev, [key]: !prev[key], all: false };
    // If nothing selected, snap back to all
    if (!Object.entries(next).some(([k, v]) => k !== 'all' && v)) next.all = true;
    return next;
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password.trim()) { toast.error('Enter admin password'); return; }
    const chosenTypes = Object.entries(types).filter(([, v]) => v).map(([k]) => k);
    const chosenStatuses = Object.entries(statuses).filter(([, v]) => v).map(([k]) => k);
    if (!window.confirm(`Delete report entries ${fromDate} → ${toDate}\nTypes: ${chosenTypes.join(', ')}\nStatuses: ${chosenStatuses.join(', ')}\n\nThis is permanent.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/report-entries/clear-range`, {
        admin_id: adminId, password,
        from_date: fromDate, to_date: toDate,
        types: chosenTypes, statuses: chosenStatuses,
      });
      const c = r.data?.counts || {};
      toast.success(`✓ Cleared: deposits=${c.deposit_deleted || 0} · withdraws=${c.withdraw_deleted || 0}`);
      setOpen(false); setPassword('');
      onCleared?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold shadow"
        data-testid="clear-report-range-btn"
        title="Clear entries by date range (admin only)"
      >
        <Trash size={12} weight="bold" /> Clear Range
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash size={18} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-red-800 text-sm">Bulk clear report entries</div>
                <div className="text-[11px] text-gray-600">Deletes deposit/withdraw rows in the selected date range.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 hover:bg-gray-100 rounded"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1"><Calendar size={10} /> From</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} disabled={busy}
                  className="w-full px-2 py-1.5 border rounded text-sm" data-testid="clear-range-from-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1"><Calendar size={10} /> To</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} disabled={busy}
                  className="w-full px-2 py-1.5 border rounded text-sm" data-testid="clear-range-to-date" />
              </div>
            </div>

            <div className="mb-3">
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Types</div>
              <div className="flex gap-1.5 flex-wrap">
                {['all', 'deposit', 'withdraw'].map(k => (
                  <button
                    key={k} type="button"
                    onClick={() => toggle(setTypes, k)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${types[k] ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-300'}`}
                    data-testid={`clear-range-type-${k}`}
                  >{k}</button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Statuses</div>
              <div className="flex gap-1.5 flex-wrap">
                {['all', 'pending', 'paid', 'cancelled', 'done'].map(k => (
                  <button
                    key={k} type="button"
                    onClick={() => toggle(setStatuses, k)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statuses[k] ? 'bg-orange-500 text-white border-orange-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    data-testid={`clear-range-status-${k}`}
                  >{k}</button>
                ))}
              </div>
            </div>

            <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1"><Lock size={10} /> Confirm with admin password</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              autoFocus disabled={busy}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder="Admin login password" data-testid="clear-range-password" />

            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setOpen(false)} disabled={busy}
                className="flex-1 py-2 rounded border border-gray-300 text-sm font-semibold hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={busy || !password.trim()}
                className="flex-1 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                data-testid="clear-range-confirm">{busy ? 'Deleting…' : 'Delete Entries'}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ClearReportRangeButton;
